import { apiGet, apiPost, apiPut } from '@forwardflow/services/Axios/configs'
import { ResponseSuccess } from '@forwardflow/types'
import { CampaignReponse, DemoLead, UpdateTimelineItemResponse, UpdateTimelineResponse } from '@forwardflow/types/demo-conversation'
import { CALL_WORKER_API, CAMPAIGN_WORKER_API, LEAD_PROCESS_WORKER_API, PHONE_WORKER_API, TEXT_WORKER_API } from '../Axios'


export class DemoConversationService {
  static mapToVariables(prompt: string, leadDetails: DemoLead, config: any) {
    const timezone = config?.organizationInfo?.timezone || 'America/New_York'
    const currentTimeVariables = this.calculateCurrentTimeVariables(
      config?.organizationInfo?.timezone || 'America/New_York'
    )
    const defaultMappings = {
      lead_firstname: leadDetails?.userInfo?.firstName,
      lead_description: leadDetails?.timelineSummary,
      lead_source: leadDetails?.sourceInfo?.name,
      lead_message_history: JSON.stringify([]),
      agent_phone: leadDetails?.assignedPhoneNumber,
      agent_name: leadDetails?.agentInfo?.name,
      calendar_availabilities: JSON.stringify(this.getDataForTimezone(timezone)),
      organization_name: config?.businessDetails?.friendlyBusinessName,
      specific_features: leadDetails?.specificFeatures ?? '',
      assistant_name: leadDetails?.assistantInfo?.name ?? '',
      lead_timeline_summary: leadDetails.timelineSummary ?? '',
      qualifying_questions: JSON.stringify(config?.qualifyingQuestions),
      usps: JSON.stringify(config?.usps),
      property_info: leadDetails?.propertyInfo?.split(',')?.slice(0, 1)?.join(',')
        ? leadDetails?.propertyInfo?.split(',')?.slice(0, 1)?.join(',')
        : leadDetails?.eventInfo?.property?.street,
      lender: leadDetails?.lenderInfo?.name,
      appointment_date_time: leadDetails?.appointmentInfo?.date,
      lead_question: leadDetails?.questions,
      lead_email: leadDetails?.userInfo?.email,
      hour: currentTimeVariables.hour,
      current_time: currentTimeVariables.current_time,
      min: currentTimeVariables.min,
      am_pm: currentTimeVariables.am_pm,
      day_phase: currentTimeVariables.day_phase,
      year: currentTimeVariables.year,
      month: currentTimeVariables.month,
      date: currentTimeVariables.date,
      day_name: currentTimeVariables.day_name,
      organization_timezone: currentTimeVariables.organization_timezone,
      // timezone: organizationInfo?.timezone,
    }

    const allMappings: any = {
      ...defaultMappings
    }

    const newString = prompt.replace(/\{([^}]+)\}/g, (match, variable) => {
      return allMappings[variable] || match
    })
    console.log('Mapped prompt:', newString)
    return newString
  }

  static generateCalendarData(timezone = 'America/New_York') {
    const now = new Date()
    const startDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }))
    startDate.setDate(startDate.getDate() + 2)

    const calendarData = []
    const timeSlots = [
      ["11:00 AM", "02:00 PM", "03:00 PM"],
      ["01:00 PM", "02:30 PM", "04:00 PM"],
      ["11:30 AM", "12:30 PM", "03:30 PM"],
      ["01:30 PM", "04:30 PM"],
      ["03:00 PM", "05:00 PM"]
    ]

    for (let i = 0; i < 5; i++) {
      const currentDate = new Date(startDate)
      currentDate.setDate(startDate.getDate() + i)

      const formattedDate = currentDate.toISOString().split('T')[0]

      calendarData.push({
        date: formattedDate,
        times: timeSlots[i] || ["10:00 AM", "03:00 PM"]
      })
    }

    return calendarData
  }

  static get data() {
    return this.generateCalendarData()
  }

  static getDataForTimezone(timezone: string): any[] {
    return this.generateCalendarData(timezone)
  }

  static calculateCurrentTimeVariables(timezone: string) {
    const now = new Date()

    const fullDateTime = now.toLocaleString('en-US', {
      timeZone: timezone,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })

    const dayName = now.toLocaleDateString('en-US', {
      timeZone: timezone,
      weekday: 'long'
    })

    const month = now.toLocaleDateString('en-US', {
      timeZone: timezone,
      month: 'long'
    })

    const year = now.toLocaleDateString('en-US', {
      timeZone: timezone,
      year: 'numeric'
    })

    const hour24 = now.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      hour12: false
    })

    const minute = now.toLocaleTimeString('en-US', {
      timeZone: timezone,
      minute: '2-digit'
    })

    const hour12 = now.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      hour12: true
    })


    const amPm = hour12.includes('AM') ? 'AM' : 'PM'
    const numericHour = parseInt(hour24.split(':')[0])
    const numericMinute = parseInt(minute)

    let dayPhase
    if (numericHour >= 5 && numericHour < 12) {
      dayPhase = 'morning'
    } else if (numericHour >= 12 && numericHour < 17) {
      dayPhase = 'afternoon'
    } else if (numericHour >= 17 && numericHour < 21) {
      dayPhase = 'evening'
    } else {
      dayPhase = 'night'
    }

    return {
      am_pm: amPm,
      current_time: fullDateTime,
      date: now.toLocaleDateString('en-US', {
        timeZone: timezone,
        day: '2-digit'
      }),
      day_name: dayName,
      day_phase: dayPhase,
      hour: numericHour,
      min: numericMinute,
      month: month,
      organization_timezone: timezone,
      year: year
    }
  }

  static getDummyLeads = async () => {
    const data = await apiGet<ResponseSuccess<DemoLead[]>>({
      apiPath: `/dummy-leads`,
      customAxiosInstance: CAMPAIGN_WORKER_API,
      includeXOrg: false,
    })
    return data.data
  }

  static async createOrUpdateLeadDetails(payload: any) {
    const response = await apiPost<ResponseSuccess<DemoLead>>({
      apiPath: `/create-or-update-dummy-lead`,
      data: payload,
      customAxiosInstance: CAMPAIGN_WORKER_API,
      includeXOrg: false,
    })
    return response
  }

  static updateLeadPreDefinedTimelineHistory = async (leadId: number, preDefinedTimelineHistory: any[]) => {
    const data = await apiPut<ResponseSuccess<UpdateTimelineResponse>>({
      apiPath: `/dummy-leads/${leadId}/pre-defined-timeline-history`,
      data: {
        preDefinedTimelineHistory
      },
      customAxiosInstance: CAMPAIGN_WORKER_API,
      includeXOrg: false,
    })
    return data.data
  }


  static updateLeadPreDefinedTimelineItem = async (
    leadId: number,
    campaignType: string,
    campaignPhase: string,
    step: number,
    updateData: any
  ) => {
    const data = await apiPut<ResponseSuccess<UpdateTimelineItemResponse>>({
      apiPath: `/dummy-leads/${leadId}/campaigns/${campaignType}/phases/${campaignPhase}/steps/${step}`,
      data: {
        updateData
      },
      customAxiosInstance: CAMPAIGN_WORKER_API,
      includeXOrg: false,
    })
    return data.data
  }

  static generateDemoSms = async (
    leadDetails: DemoLead,
    timelineHistory: any[],
    prompt: string,
    config?: any
  ) => {
    const mappedPrompt = await this.mapToVariables(prompt, leadDetails, config)
    const data = await apiPost<ResponseSuccess<any>>({
      apiPath: `/generate-sms-content`,
      data: {
        leadData: {
          leadDetails: leadDetails,
          timelineHistory: timelineHistory
        },
        prompt: mappedPrompt
      },
      customAxiosInstance: TEXT_WORKER_API,
      includeXOrg: false,
    })
    return data.data
  }

  static async generateDemoCall(basePrompt: string, voicemailBasePrompt: string, leadDetails: DemoLead, config?: any, shouldSendVoicemail?: boolean) {
    const mappedPrompt = await this.mapToVariables(basePrompt, leadDetails, config)

    let voicemailContent = null

    if (shouldSendVoicemail && voicemailBasePrompt && voicemailBasePrompt.trim() !== "") {
      const voicemailPrompt = await this.mapToVariables(
        voicemailBasePrompt,
        leadDetails,
        config
      )
      voicemailContent = await this.generateVoicemailPrompt(voicemailPrompt)
    }

    const payload = {
      customer: {
        number: leadDetails?.userInfo?.phone
      },
      prompt: mappedPrompt,
      phoneNumber: {
        twilioPhoneNumber: '+17198009861'
      },
      forwardedTo: {
        number: leadDetails?.agentInfo?.phone ?? ''
      },
      agentId: 'BL0wXSIlASA7YugkorTv',
      apiKey: 'sk_d21d8b57284bfb5e640dccc04b481ecceda162899f50c5a8',
      metadata: {
        ...leadDetails,
        stepId: 104,
        orgId: 'test-dummy-org',
        voiceConfig: {
          voiceId: leadDetails?.assistantInfo?.id,
          assistantName: leadDetails?.assistantInfo?.name,
          voiceModel: 'gemini-2.0-flash-001',
          voiceSimilarityBoost: 0.4,
          voiceSpeed: 1.0,
          modelId: "eleven_flash_v2"
        }
      },
      voicemailDetection: {
        provider: 'twilio',
        enabled: true
      },
      voicemailMessage: voicemailContent ?? null
    }

    const response = await apiPost<ResponseSuccess<any>>({
      apiPath: `/dummy-call`,
      customAxiosInstance: CALL_WORKER_API,
      data: payload,
      includeXOrg: false,
    })
    return {
      ...(response?.data ?? {}),
      voicemailContent: voicemailContent
    }
  }

  static async generateVoicemailPrompt(prompt: string) {
    const response = await apiPost<ResponseSuccess<{ content: string }>>({
      apiPath: `/generate-voicemail-content`,
      customAxiosInstance: PHONE_WORKER_API,
      data: {
        prompt: prompt
      },
      includeXOrg: false,
    })
    return response?.data?.content ?? ''
  }

  static async processDemoConversation(campaignDemoType: string | null, timelineHistory: any[], leadDetails: DemoLead) {
    const response = await apiPost<ResponseSuccess<any>>({
      apiPath: `/process-demo-conversation`,
      customAxiosInstance: LEAD_PROCESS_WORKER_API,
      data: {
        "message": {
          "leadDetails": leadDetails,
          "timelines": timelineHistory,
          "campaignDemoType": campaignDemoType,
          "availableStages": [
            "ff-hot-prospect",
            "ff-warm-prospect",
            "ff-cold-prospect",
            "ff-long-term",
            "ff-do-not-contact",
            "ff-under-contract",
            "ff-deal-closed",
            "ff-appointment-set",
            "ff-appointment-missed",
            "ff-lead",
            "ff-reactive"
          ],
          "availableCampaigns": [
            "ff-hot-prospect",
            "ff-warm-prospect",
            "ff-cold-prospect",
            "ff-long-term",
            "ff-do-not-contact",
            "ff-under-contract",
            "ff-deal-closed",
            "ff-appointment-set",
            "ff-appointment-missed",
            "ff-lead",
            "Active Conversation",
            "ff-reactive"
          ]
        },
      },
      includeXOrg: false,
    })
    return response.data
  }

  static async postDemoCallAnalysis(endOfReportData: any) {
    const response = await apiPost<ResponseSuccess<any>>({
      apiPath: `/post-call-analysis-demo-conversation`,
      customAxiosInstance: LEAD_PROCESS_WORKER_API,
      data: endOfReportData,
      includeXOrg: false,
    })
    return response.data
  }

  static async getCampaignsByType(type: string) {
    const data = await apiGet<ResponseSuccess<CampaignReponse>>({
      apiPath: `/campaign-steps?type=${type}`,
      customAxiosInstance: CAMPAIGN_WORKER_API,
      includeXOrg: false,
    })
    return data?.data
  }

  static async getActiveConversationScript(type: string, phase: string, platform: string) {
    const data = await apiGet<ResponseSuccess<any>>({
      apiPath: `/active-conversation-script?type=${type}&campaignPhase=${phase}&platform=${platform}`,
      customAxiosInstance: CAMPAIGN_WORKER_API,
      includeXOrg: false,
    })
    return data?.data
  }
}